import React from 'react';
import {
  ExternalLink,
  LineChart,
  Pause,
  Play,
  Trash2,
  Bell,
  Clock,
  Store,
} from 'lucide-react';
import type { WatchlistViewItem } from './types';
import { formatCurrencyPrice } from '../../utils/currency';
import { formatRelativeTime } from '../../utils/dates';
import { StatusBadge } from '../../components/common';
import { useLanguage } from '../../contexts/LanguageContext';

export interface WatchlistCardProps {
  watch: WatchlistViewItem;
  onTogglePause: (watch: WatchlistViewItem) => void;
  onDelete: (watch: WatchlistViewItem) => void;
  onViewHistory: (watch: WatchlistViewItem) => void;
}

export const WatchlistCard: React.FC<WatchlistCardProps> = ({
  watch,
  onTogglePause,
  onDelete,
  onViewHistory,
}) => {
  const { t } = useLanguage();

  const formattedCurrentPrice = formatCurrencyPrice(
    watch.currentPrice,
    watch.establishedCurrency
  );
  const formattedListPrice = watch.listPrice
    ? formatCurrencyPrice(watch.listPrice, watch.establishedCurrency)
    : null;

  const hasDiscount = watch.discountPct !== null && watch.discountPct > 0;
  const merchantDisplayName = watch.merchantName || watch.merchantDomain || 'Store';

  return (
    <div
      onClick={() => onViewHistory(watch)}
      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs hover:shadow-md hover:border-emerald-500/40 dark:hover:border-emerald-500/40 transition-all duration-200 flex flex-col justify-between p-5 group cursor-pointer"
    >
      {/* Top Header */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 font-medium">
            <Store className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
            <span className="truncate max-w-[150px] font-semibold text-slate-700 dark:text-slate-300">
              {merchantDisplayName}
            </span>
          </div>
          <StatusBadge status={watch.isActive ? 'ACTIVE' : 'PAUSED'} />
        </div>

        {/* Product Title */}
        <h3
          title={watch.productTitle}
          className="text-sm font-semibold text-slate-900 dark:text-slate-100 line-clamp-2 mb-3 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors"
        >
          {watch.productTitle}
        </h3>

        {/* Pricing Block */}
        <div className="mb-4">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
              {formattedCurrentPrice}
            </span>
            {formattedListPrice && (
              <span className="text-xs text-slate-400 line-through">
                {formattedListPrice}
              </span>
            )}
            {hasDiscount && (
              <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800">
                {Math.round(watch.discountPct!)}% OFF
              </span>
            )}
          </div>

          {/* Alert target info */}
          {watch.alertRule?.targetPrice && (
            <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 text-[11px] font-medium border border-emerald-200 dark:border-emerald-800">
              <Bell className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
              <span>
                Alert at {formatCurrencyPrice(watch.alertRule.targetPrice, watch.establishedCurrency)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Card Footer & Actions */}
      <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-1 text-[11px] text-slate-400">
          <Clock className="w-3 h-3" />
          <span>{formatRelativeTime(watch.lastSuccessAt)}</span>
        </div>

        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onTogglePause(watch);
            }}
            className="p-1.5 rounded-lg text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title={watch.isActive ? t('card.pauseTracking') : t('card.resumeTracking')}
            aria-label={watch.isActive ? 'Pause' : 'Resume'}
          >
            {watch.isActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onViewHistory(watch);
            }}
            className="p-1.5 rounded-lg text-slate-500 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title={t('card.viewDetails')}
            aria-label="View History"
          >
            <LineChart className="w-4 h-4" />
          </button>

          {watch.requestedUrl && (
            <a
              href={watch.requestedUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-1.5 rounded-lg text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              title={t('card.visitStore')}
              aria-label="Open retailer store"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(watch);
            }}
            className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
            title={t('card.delete')}
            aria-label="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
